import { createRef, FC, useEffect, useRef, useState } from 'react'
import ValidatorWrapper, { ValidatorField } from '@coxy/react-validator'

import validatorRules from 'libs/validator-rules'

import InputCalculator from 'components/ui/calculator/components/input-exchange-calculator'
import Notice from 'components/ui/notice'
import ButtonBlue from 'components/ui/button/_color/_blue'
import cn from 'classnames'

import { ICurrency, ICurrencyWithBalanceAndRate } from 'store/fetchers/currencies'

import { useAppDispatch, useAppSelector } from 'store/hooks'
import { getCurrencies } from 'store/reducers/currencies'
import { allWallets, allWalletsRates, fetchAllWallets, selectedWallets, setActiveWallet } from 'store/reducers/wallets'

import { formatCurrency, formatNumber } from 'utils/formatters'
import { useMultipleCurrency } from 'utils/use-currency'

import { useRouter } from 'next/router'

import { _estimateExchange } from 'store/fetchers/wallets'

import { useTranslation } from 'utils/use-translation'

import { LoginStatuses, selectedAuthStatus, selectedUser } from 'store/reducers/users'

import { ReactSVG } from 'react-svg'

import { roundNumber, roundNumberWithPrecision } from '../../../../utils/round-number'

import { floorNumber } from '../../../../utils/floor-number'

import PercentSlider from '../../../../components/ui/percent-slider'

import { TransactionTab } from './components/transaction-tab'
import TopUpIcon from './icons/Receive.svg'
import WrapIcon from './icons/wrap.svg'

import styles from './styles.module.scss'

const DEFAULT_FROM = '0'
const DEFAULT_USD = 150

const ExchangePage: FC<{ validatorRule?: string; showBalance?: boolean; listFullWidth?: boolean }> = ({
  validatorRule = 'exchangeDepositAmount',
  showBalance = false,
  listFullWidth = false,
}) => {
  const { t, replacePlaceholders } = useTranslation()

  const validator = createRef<ValidatorWrapper>()
  const timer = useRef<any>() // cannot set NodeJS.Timeout
  const dispatch = useAppDispatch()
  const router = useRouter()

  const authStatus = useAppSelector(selectedAuthStatus)
  const user = useAppSelector(selectedUser)
  const currencies = useAppSelector(getCurrencies)
  const wallets = useAppSelector(allWallets)
  const walletsCurrencies = useMultipleCurrency(currencies.filter((c) => c.is_allowed_for_wallet))
  const walletRates = useAppSelector(allWalletsRates)
  const [from, setFrom] = useState<ICurrencyWithBalanceAndRate>(null)
  const [isLoadingFrom, setLoadingFrom] = useState(false)
  const [_amountFrom, _setAmountFrom] = useState(DEFAULT_FROM)
  const [rateFrom, setRateFrom] = useState('0')
  const availableWallets = useAppSelector(selectedWallets)

  const amountFrom = useRef(_amountFrom)

  const [to, setTo] = useState<ICurrency>(null)
  const [isLoadingTo, setLoadingTo] = useState(true)
  const [amountTo, setAmountTo] = useState('0')
  const [rateTo, setRateTo] = useState('0')

  const [listExchangeCurrencies, setListExchangeCurrencies] = useState<ICurrencyWithBalanceAndRate[]>([])
  const [filteredListExchangeCurrencies, setFileteredListExchangeCurrencies] = useState<ICurrencyWithBalanceAndRate[]>(
    [],
  )
  const [listWalletsCurrencies, setListWalletsCurrencies] = useState<ICurrencyWithBalanceAndRate[]>([])
  const [estimateError, setEstimateError] = useState('')

  const [isInitialLoadCurrency, setIsInitialLoadCurrency] = useState<boolean>(true)
  const [isInitialEstimate, setIsInitialEstimate] = useState<boolean>(true)

  const [estimatedRate, setEstimatedRate] = useState(0)
  const [defaultFrom, setDefaultFrom] = useState<number>(+DEFAULT_FROM)

  const [amountPercent, setAmountPercent] = useState<number>(0)
  const [percentCoefficient] = useState<number>(2)

  const [areValid, setAreValid] = useState(false)

  const [isLoading, setIsLoading] = useState(false)
  const [isBtnPressed, setIsBtnPressed] = useState(false)
  const isPlacesChanged = useRef(false)

  const refetchTimeoutRef = useRef(null)
  const refetchIntervalRef = useRef(null)

  useEffect(() => {
    if (authStatus === LoginStatuses.LOGIN && user) {
      const fetchData = () => {
        dispatch(fetchAllWallets())
      }
      const now = new Date()
      const delay = 30000 - (now.getTime() % 30000)

      fetchData()

      refetchTimeoutRef.current = setTimeout(() => {
        refetchIntervalRef.current = setInterval(fetchData, 30000)
      }, delay)
    }

    return () => {
      clearTimeout(refetchTimeoutRef.current)
      clearInterval(refetchIntervalRef.current)
    }
  }, [authStatus, user])

  useEffect(() => {
    if (to) {
      prepareEstimate()
    }
  }, [to])

  useEffect(() => {
    const timerId = setTimeout(() => {
      runCalculation()
    }, 350)

    return () => {
      clearTimeout(timerId)
    }
  }, [_amountFrom])

  useEffect(() => {
    if (listWalletsCurrencies.length && isInitialLoadCurrency && walletsCurrencies.length) {
      const filteredCurrencies: ICurrency[] = []
      let withBalance = listWalletsCurrencies.filter((cur) => +cur.balance !== 0)

      let withoutBalance = listWalletsCurrencies.filter((cur) => +cur.balance === 0)
      withBalance = withBalance.sort((a, b) => +b.balance * +b.rate - +a.balance * +a.rate)
      withoutBalance = withoutBalance.sort((a, b) => {
        if (a.exchange_priority === null) return 1
        if (b.exchange_priority === null) return -1
        return a.exchange_priority - b.exchange_priority
      })
      filteredCurrencies.push(...withBalance)
      filteredCurrencies.push(...withoutBalance)

      if (
        router.query.fromCurrency &&
        router.query.fromNetwork &&
        walletsCurrencies &&
        walletsCurrencies.length &&
        listWalletsCurrencies &&
        listWalletsCurrencies.length
      ) {
        if (
          filteredCurrencies.find(
            (item) =>
              item.code.toLowerCase() === router.query.fromCurrency.toString().toLowerCase() &&
              item.network.toLowerCase() === router.query.fromNetwork.toString().toLowerCase(),
          )
        ) {
          setCurrency(
            filteredCurrencies.filter(
              (item) =>
                item.code.toLowerCase() === router.query.fromCurrency.toString().toLowerCase() &&
                item.network.toLowerCase() === router.query.fromNetwork.toString().toLowerCase(),
            )[0],
          )
          sessionStorage.setItem(
            'exchange:FromCoinCode',
            filteredCurrencies.filter(
              (item) =>
                item.code.toLowerCase() === router.query.fromCurrency.toString().toLowerCase() &&
                item.network.toLowerCase() === router.query.fromNetwork.toString().toLowerCase(),
            )[0].code,
          )
          sessionStorage.setItem(
            'exchange:FromCoinNetwork',
            filteredCurrencies.filter(
              (item) =>
                item.code.toLowerCase() === router.query.fromCurrency.toString().toLowerCase() &&
                item.network.toLowerCase() === router.query.fromNetwork.toString().toLowerCase(),
            )[0].network,
          )
        } else {
          setCurrency(filteredCurrencies.filter((item) => !item.is_stable)[0])
        }
      } else {
        setCurrency(filteredCurrencies.filter((item) => !item.is_stable)[0])
      }

      if (
        router.query.toCurrency &&
        router.query.toNetwork &&
        walletsCurrencies &&
        walletsCurrencies.length &&
        listWalletsCurrencies &&
        listWalletsCurrencies.length
      ) {
        if (
          filteredCurrencies.find(
            (item) =>
              item.code.toLowerCase() === router.query.toCurrency.toString().toLowerCase() &&
              item.network.toLowerCase() === router.query.toNetwork.toString().toLowerCase(),
          )
        ) {
          setTo(
            filteredCurrencies.filter(
              (item) =>
                item.code.toLowerCase() === router.query.toCurrency.toString().toLowerCase() &&
                item.network.toLowerCase() === router.query.toNetwork.toString().toLowerCase(),
            )[0],
          )
          sessionStorage.setItem(
            'exchange:ToCoinCode',
            filteredCurrencies.filter(
              (item) =>
                item.code.toLowerCase() === router.query.toCurrency.toString().toLowerCase() &&
                item.network.toLowerCase() === router.query.toNetwork.toString().toLowerCase(),
            )[0].code,
          )
          sessionStorage.setItem(
            'exchange:ToCoinNetwork',
            filteredCurrencies.filter(
              (item) =>
                item.code.toLowerCase() === router.query.toCurrency.toString().toLowerCase() &&
                item.network.toLowerCase() === router.query.toNetwork.toString().toLowerCase(),
            )[0].network,
          )
        } else {
          setTo(filteredCurrencies.filter((item) => !item.is_stable)[1])
        }
      } else {
        setTo(filteredCurrencies.filter((item) => !item.is_stable)[1])
      }

      setFileteredListExchangeCurrencies(filteredCurrencies)

      // Load saved values from sessionStorage
      const savedFromAmount = sessionStorage.getItem('exchange:FromAmount')
      const savedFromCoinCode = sessionStorage.getItem('exchange:FromCoinCode')
      const savedFromCoinNetwork = sessionStorage.getItem('exchange:FromCoinNetwork')
      const savedToCoinCode = sessionStorage.getItem('exchange:ToCoinCode')
      const savedToCoinNetwork = sessionStorage.getItem('exchange:ToCoinNetwork')

      if (savedFromCoinCode && savedFromCoinNetwork) {
        const savedFromCoin = filteredCurrencies.find(
          (currency) => currency.code === savedFromCoinCode && currency.network === savedFromCoinNetwork,
        )
        if (savedFromCoin) {
          setCurrency(savedFromCoin)
        }
      } else {
        setCurrency(filteredCurrencies.filter((item) => !item.is_stable)[0])
      }

      if (savedToCoinCode && savedToCoinNetwork) {
        const savedToCoin = filteredCurrencies.find(
          (currency) => currency.code === savedToCoinCode && currency.network === savedToCoinNetwork,
        )
        if (savedToCoin) {
          setTo(savedToCoin)
        } else {
          setTo(filteredCurrencies.filter((item) => !item.is_stable)[1])
        }
      } else {
        setTo(filteredCurrencies.filter((item) => !item.is_stable)[1])
      }

      if (savedFromAmount) {
        handleSetAmountFrom(savedFromAmount)
      }
      setIsInitialLoadCurrency(false)
    }
  }, [listWalletsCurrencies, walletsCurrencies])

  useEffect(() => {
    if (listExchangeCurrencies.length && wallets.length) {
      const walletsCurrenciesWithBalance: ICurrencyWithBalanceAndRate[] = walletsCurrencies.map((currency) => {
        const wallet = wallets.find(
          (wallet) =>
            wallet.code?.toLowerCase() === currency.code?.toLowerCase() &&
            wallet.network?.toLowerCase() === currency.network?.toLowerCase(),
        )
        if (wallet) {
          return { ...currency, balance: wallet.balance, rate: wallet.rate }
        }
        return currency
      })

      setListWalletsCurrencies(walletsCurrenciesWithBalance)
    }
  }, [listExchangeCurrencies, wallets])

  useEffect(() => {
    if (from) {
      const roundedBalance = floorNumber(from.balance, 6)

      const savedFromAmount = sessionStorage.getItem('exchange:FromAmount')
      setDefaultFrom(roundedBalance)

      if (router.query.fromCurrency && router.query.fromNetwork) {
        if (!isPlacesChanged.current) {
          setAmountFrom(roundedBalance.toString())
        }

        if (roundedBalance === 0) {
          setAmountPercent(0)
        } else {
          setAmountPercent(100)
        }
      } else if (!isPlacesChanged.current) {
        if (isInitialEstimate) {
          setAmountFrom(Math.min(Number(savedFromAmount ?? roundedBalance), roundedBalance).toString())
        } else {
          setAmountFrom(roundedBalance.toString())
        }
      }
    }

    isPlacesChanged.current = false
  }, [from])

  useEffect(() => {
    setEstimateError('')
    setLoadingFrom(true)
    setLoadingTo(true)
    if (currencies.length && wallets.length) {
      const filtered = currencies.filter((c) => c.is_allowed_for_wallet)
      setListExchangeCurrencies(filtered)
    }
    setLoadingFrom(false)
    setLoadingTo(false)
  }, [currencies, wallets])

  const handleCreateExchange = async () => {
    setIsLoading(true)

    const estimatedExchange = await _estimateExchange({
      fromCurrency: from.code,
      fromNetwork: from.network,
      toCurrency: to.code,
      toNetwork: to.network,
      fromAmount: String(_amountFrom),
    })

    if (!estimatedExchange) {
      setAreValid(false)
      setIsLoading(false)
      return
    }

    setIsLoading(false)

    await router.push({
      pathname: 'exchange/exchange/confirm',
      query: estimatedExchange,
    })
  }

  const handleSetFrom = (from: ICurrency) => {
    setCurrency(from)
  }

  const setAmountFrom = (value: string) => {
    _setAmountFrom(value)
    amountFrom.current = value
    localStorage.setItem('exchange:FromAmount', value)
  }

  const handleSetTo = (to: ICurrency) => {
    setTo(to)
  }

  const prepareEstimate = async () => {
    setLoadingTo(true)
    if (+_amountFrom <= 0.0000001 || _amountFrom === '') {
      const estimatedExchange = await _estimateExchange({
        fromCurrency: from.code,
        fromNetwork: from.network,
        toCurrency: to.code,
        toNetwork: to.network,
        fromAmount: '1',
      })
      if (!estimatedExchange) {
        setAreValid(false)
      }
      setAmountTo('0')
      estimatedExchange && calculateEstimatedRate(estimatedExchange)
    } else {
      const estimatedExchange = await _estimateExchange({
        fromCurrency: from.code,
        fromNetwork: from.network,
        toCurrency: to.code,
        toNetwork: to.network,
        fromAmount: _amountFrom,
      })
      if (!estimatedExchange) {
        setAreValid(false)
      }

      setAmountTo(floorNumber(estimatedExchange?.toAmount, 6)?.toString() || '0')
      estimatedExchange && calculateEstimatedRate(estimatedExchange)
    }

    // Save values to sessionStorage
    if (!isInitialEstimate || (router.query.fromCurrency && router.query.fromNetwork)) {
      sessionStorage.setItem('exchange:FromAmount', _amountFrom)
      sessionStorage.setItem('exchange:FromCoinCode', from.code)
      sessionStorage.setItem('exchange:FromCoinNetwork', from.network)
      sessionStorage.setItem('exchange:ToCoinCode', to.code)
      sessionStorage.setItem('exchange:ToCoinNetwork', to.network)
    }
    setIsInitialEstimate(false)
    setTimeout(() => {
      setLoadingTo(false)
    }, 100)
  }

  const handleSetAmountFrom = (amount: string) => {
    if (!isInitialEstimate) {
      setAmountFrom(amount)
    } else {
      const savedFromAmount = sessionStorage.getItem('exchange:FromAmount')

      setAmountFrom(
        Number(from?.balance) !== 0
          ? Math.min(Number(savedFromAmount ?? amount), floorNumber(from?.balance ?? defaultFrom, 6)).toString()
          : Number(savedFromAmount).toString(),
      )
    }
  }

  useEffect(() => {
    if (walletsCurrencies.length) {
      if (!isInitialEstimate) {
        if (Number(floorNumber(defaultFrom, 6)) === 0) {
          setAmountPercent(0)
        } else {
          if (+_amountFrom >= floorNumber(defaultFrom, 6)) {
            setAmountPercent(100)
          } else {
            const percent = (+_amountFrom / floorNumber(defaultFrom, 6)) * 100

            setAmountPercent(Math.round(percent))
          }
        }
      } else {
        const savedFromAmount = sessionStorage.getItem('exchange:FromAmount')

        if (!router.query.fromCurrency && !router.query.fromNetwork) {
          if (savedFromAmount) {
            if (Number(floorNumber(defaultFrom, 6)) === 0) {
              setAmountPercent(0)
            } else {
              if (+savedFromAmount >= floorNumber(defaultFrom, 6)) {
                setAmountPercent(100)
              } else {
                const percent = (+savedFromAmount / defaultFrom) * 100
                setAmountPercent(Math.round(percent))
              }
            }
          } else {
            if (Number(floorNumber(defaultFrom, 6)) === 0) {
              setAmountPercent(0)
            } else {
              if (+_amountFrom >= floorNumber(defaultFrom, 6)) {
                setAmountPercent(100)
              } else {
                const percent = (+_amountFrom / defaultFrom) * 100
                setAmountPercent(Math.round(percent))
              }
            }
          }
        }
      }
    }
  }, [defaultFrom, _amountFrom, walletsCurrencies, from])

  const calculateEstimatedRate = (estimate) => {
    const { fromAmount, toAmount } = estimate
    setEstimatedRate(fromAmount === 1 ? toAmount : toAmount / fromAmount)
  }

  const handleSetAmountTo = () => {
    clearTimeout(timer.current)
    setAmountTo(amountTo)
    prepareEstimate()
  }

  const runCalculation = () => {
    from && prepareEstimate()
  }

  const setCurrency = (from: ICurrency) => {
    if (walletsCurrencies && walletsCurrencies.length && listWalletsCurrencies && listWalletsCurrencies.length) {
      const currentCurrency = listWalletsCurrencies.find((el) => el.code === from.code && el.network === from.network)
      if (currentCurrency) {
        let roundedNumber =
          floorNumber(currentCurrency.balance, 3) !== 0
            ? currentCurrency.balance
            : floorNumber(DEFAULT_USD / +currentCurrency.rate, 6).toString()

        roundedNumber = roundNumber(parseFloat(roundedNumber?.toString()))
        const savedFromAmount = sessionStorage.getItem('exchange:FromAmount')

        if (!isInitialEstimate || !savedFromAmount) {
          floorNumber(currentCurrency.balance, 3) !== 0
            ? setAmountFrom(roundedNumber.toString())
            : setAmountFrom(floorNumber(DEFAULT_USD / +currentCurrency.rate, 3).toString())
        } else {
          setAmountFrom(savedFromAmount)
        }
        setFrom(from)
      } else {
        setAmountFrom(DEFAULT_FROM)
        setFrom(from)
      }
    }
  }

  const changePercent = (percent: number) => {
    setAmountFrom(roundNumberWithPrecision((+defaultFrom * percent) / 100, 6).toString())

    setAmountPercent(percent)
  }

  useEffect(() => {
    if (!walletRates) return
    const keyFrom = `${from?.code.toLowerCase()}-${from?.network.toLowerCase()}`
    const keyTo = `${to?.code.toLowerCase()}-${to?.network.toLowerCase()}`

    if (walletRates[keyFrom]) {
      setRateFrom(walletRates[keyFrom].toString())
    } else {
      setRateFrom('0')
    }

    if (walletRates[keyTo]) {
      setRateTo(walletRates[keyTo].toString())
    } else {
      setRateTo('0')
    }
  }, [from, to, walletRates])

  const notEnoughBalanceClick = () => {
    if (
      availableWallets &&
      availableWallets.find(
        (el) =>
          el.code?.toLowerCase() === from?.code?.toLowerCase() &&
          el.network?.toLowerCase() === from?.network?.toLowerCase(),
      )
    ) {
      dispatch(
        setActiveWallet(
          availableWallets &&
            availableWallets.find(
              (el) =>
                el.code?.toLowerCase() === from?.code?.toLowerCase() &&
                el.network?.toLowerCase() === from?.network?.toLowerCase(),
            ),
        ),
      )

      router.push(`/account/wallets/info/?code=${from?.code}&network=${from?.network}`)
    } else {
      router.push('/account/wallets')
    }
  }

  const changeCoinsPlaces = () => {
    const temp = from
    isPlacesChanged.current = true

    setFrom(to)
    setTo(temp)
  }

  const handlePressStart = () => setIsBtnPressed(true)
  const handlePressEnd = () => setIsBtnPressed(false)

  return (
    <div className={styles.wrapper}>
      <ValidatorWrapper ref={validator}>
        <ValidatorField value={amountFrom.current} rules={validatorRules[validatorRule](from, t)}>
          {({ isValid, message }) => {
            setAreValid(isValid)
            return (
              <InputCalculator
                onEnter={handleCreateExchange}
                coin={from}
                isLoading={isLoadingFrom}
                onChangeAmount={handleSetAmountFrom}
                onChangeCoin={handleSetFrom}
                value={_amountFrom}
                rate={rateFrom}
                label={t('you_send')}
                list={filteredListExchangeCurrencies}
                className={styles.changeCoinWrapper}
                labelClassName={styles.inputLabel}
                inputClassName={styles.inputInput}
                inputWrapperClassName={cn(styles.inputWrapper, styles.inputWrapper_first)}
                bigCurBlock
                showBalance={showBalance}
                listFullWidth={listFullWidth}
                inputMode="decimal"
              >
                {(estimateError ||
                  (!isValid && !isLoadingFrom && (amountTo !== '—' || amountFrom.current !== '—'))) && (
                  <div className={styles.inputError}>
                    {estimateError || t(message)}{' '}
                    {t(message) === t('not_enough_balance_for_exchange') && (
                      <span className={styles.inputError__link} onClick={notEnoughBalanceClick}>
                        <img className={styles.topUpIcon} src={TopUpIcon} alt="top up icon" />
                        {replacePlaceholders(t('top_up_wallet'), { code: from.code })}
                      </span>
                    )}
                  </div>
                )}
                {_amountFrom &&
                  rateFrom &&
                  !estimateError &&
                  isValid &&
                  !isLoadingFrom &&
                  !(!isValid && !isLoadingFrom && (amountTo !== '—' || amountFrom.current !== '—')) && (
                    <div className={styles.balanceInUsd}>
                      {floorNumber(Number(_amountFrom) * Number(rateFrom), 6) !== 0 && (
                        <span>~${floorNumber(Number(_amountFrom) * Number(rateFrom), 2)}</span>
                      )}

                      {floorNumber(Number(_amountFrom) * Number(rateFrom), 6) === 0 && (
                        <span>&nbsp;${floorNumber(Number(_amountFrom) * Number(rateFrom), 2)}</span>
                      )}
                    </div>
                  )}
                <PercentSlider
                  amountPercent={amountPercent}
                  percentCoefficient={percentCoefficient}
                  defaultFrom={Number(defaultFrom)}
                  changePercent={changePercent}
                  className={styles.percentSlider}
                />
              </InputCalculator>
            )
          }}
        </ValidatorField>

        <button
          className={cn(styles['wrap-btn'], { [styles['wrap-btn__active']]: isBtnPressed })}
          onClick={changeCoinsPlaces}
          onPointerDown={handlePressStart}
          onPointerUp={handlePressEnd}
          onPointerLeave={handlePressEnd}
        >
          <ReactSVG src={WrapIcon} width={28} height={28} />
        </button>

        <ValidatorField value={amountTo}>
          {({ isValid, message }) => {
            return (
              <InputCalculator
                inputMode="decimal"
                onEnter={handleCreateExchange}
                coin={to}
                isLoading={isLoadingTo}
                onChangeAmount={handleSetAmountTo}
                onChangeCoin={handleSetTo}
                value={amountTo}
                rate={rateTo}
                label={t('you_get')}
                list={filteredListExchangeCurrencies}
                className={cn(styles.changeCoinWrapper, styles.changeCoinWrapper_to)}
                labelClassName={styles.inputLabel}
                inputClassName={styles.inputInput}
                inputWrapperClassName={styles.inputWrapper}
                bigCurBlock
                showBalance={showBalance}
                listFullWidth={listFullWidth}
                loaderClassName={styles.loaderTo}
                readonly
              >
                {!estimateError && !isValid && !isLoadingTo && (amountTo !== '—' || amountFrom.current !== '—') && (
                  <div className={styles.inputError}>{t(message)}</div>
                )}
                {amountTo && rateTo && !estimateError && isValid && !isLoadingTo && (
                  <div className={cn(styles.balanceInUsd, styles.balanceInUsd_to)}>
                    {floorNumber(
                      Number(+_amountFrom <= 0.0000001 || _amountFrom === '' ? '0' : amountTo) * Number(rateTo),
                      2,
                    ) !== 0 && (
                      <span>
                        ~$
                        {formatNumber(
                          floorNumber(
                            Number(+_amountFrom <= 0.0000001 || _amountFrom === '' ? '0' : amountTo) * Number(rateTo),
                            2,
                          ),
                        )}
                      </span>
                    )}

                    {floorNumber(
                      Number(+_amountFrom <= 0.0000001 || _amountFrom === '' ? '0' : amountTo) * Number(rateTo),
                      2,
                    ) === 0 && (
                      <span>
                        $
                        {formatNumber(
                          floorNumber(
                            Number(+_amountFrom <= 0.0000001 || _amountFrom === '' ? '0' : amountTo) * Number(rateTo),
                            2,
                          ),
                        )}
                      </span>
                    )}
                  </div>
                )}
              </InputCalculator>
            )
          }}
        </ValidatorField>
        <div className={styles.line__mobile}></div>
        <div className={styles.info}>
          <div className={styles.infoItem}>
            <div className={styles.info_rightSide}>
              <div className={styles.infoItemTitle}>{t('estimated_rate')}</div>
              <Notice leftPos={'0'} alt={t('estimated_rate_notice')}>
                {t('estimated_rate_notice_info')}
              </Notice>
            </div>
            <div className={styles.infoItemValue}>
              {1} {formatCurrency(from, true)} <br /> ~{' '}
              {estimatedRate && isFinite(estimatedRate) ? floorNumber(estimatedRate, 6) : ''} {formatCurrency(to, true)}
            </div>
          </div>

          <div className={styles.verticalLine}></div>

          <div className={styles.infoItem}>
            <div className={styles.infoItemTitle}>{t('estimated_arrival')}</div>
            <div className={styles.infoItemValue}>{t('arrival_time')}</div>
          </div>

          <div className={styles.verticalLine}></div>

          <div className={styles.infoItem}>
            <div className={styles.info_rightSide}>
              <div className={styles.infoItemTitle}>{t('conditions')}</div>
              <Notice leftPos={'0'} alt={t('fees_notice')}>
                {t('rate_includes_fees')}
              </Notice>
            </div>
            <div className={styles.infoItemValue}>{t('no_hidden_fees')}</div>
          </div>
        </div>
        <ButtonBlue
          className={styles.button}
          isLoading={isLoading}
          onClick={() => {
            areValid && handleCreateExchange()
          }}
          disabled={!areValid || _amountFrom === '0' || amountTo === '0' || isLoading}
        >
          {t('exchange_tab')}
        </ButtonBlue>
      </ValidatorWrapper>
      <TransactionTab />
    </div>
  )
}

export default ExchangePage
